/* Angular + external */
import { ChangeDetectionStrategy, Component, QueryList, Renderer2 } from "@angular/core";
import { ViewChildren } from "@angular/core";
import { ChangeDetectorRef } from "@angular/core";
/***********************/
/* BFX */
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// cubiq-services
import {
  MarketDataEventTransformer,
  RateSubscriptionPromiseService,
  TraderGroupPromiseLookupService,
  RateCache,
  CcyPairPromiseLookupService,
  RatePromiseService,
  CurrencyPairCalendarPromiseLookupService,
  SystemConfigPromiseService,
  BranchLookupService,
  ClientLookupService,
} from "shared/cubiq-services";

// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
// order-entry services
import { OESettingsService } from "../services/basic/oe-settings.service";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OEApiService } from "../services/oe-api.service";
import { OEUtilityService } from "../services/basic/oe-utility.service";
// order-entry  models
import { OEInputLockStateItemModel } from "../data/models/oe-locking-profile.model";
import { OEInputModel } from "../data/models/oe-input.model";
//  order-entry  enums
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENPanel } from "../data/enums/oe-panel.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
// interface controls components
// interface controls enums
import { ICNInputControlState } from "./controls/models/bfx-interface-controls.enums";
// interface controls models
import {
  ICAutocompleteGenericModel,
  ICFixingSourceModel,
  ICTenorValueDateModel,
} from "./controls/models/bfx-interface-controls.models";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { OESpotEntryComponent } from "./oe-panel-spot-entry.component";
import { OEPanelNdfModel } from "../data/models/oe-panel-ndf.model";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";
import { OELoggerService } from "../services/basic/oe-logger.service";
import { BfxCalendarComponent } from "./controls/good-from-until/bfx-calendar.component";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { OEDatasetService } from "app/order-entry/services/basic/oe-dataset.service";
import { OEAdapterForwardService } from "../services/adapter/oe-adapter-forward.service";
import { OEModelGetterService } from "../services/model/oe-model-getter.service";

@Component({
  selector: "oe-panel-ndf-entry",
  templateUrl: "./html/oe-panel-ndf-entry.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OENdfEntryComponent extends OESpotEntryComponent {
  @ViewChildren("inputFixingSource")
  fixingSourceControl: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputFixingDate")
  fixingDateControl: QueryList<BfxCalendarComponent>;

  public data: OEPanelNdfModel = new OEPanelNdfModel();

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    translate: TranslateService,
    settings: OESettingsService,
    coordinator: OECoordinatorService,
    traderGroupService: TraderGroupPromiseLookupService,
    getter: OEModelGetterService,
    forwardAdapter: OEAdapterForwardService,
    utility: OEUtilityService,
    api: OEApiService,
    cd: ChangeDetectorRef,
    //
    rateService: RatePromiseService,
    rateCache: RateCache,
    rateTransformer: MarketDataEventTransformer,
    rateSubscriptionService: RateSubscriptionPromiseService,
    currencyPairService: CcyPairPromiseLookupService,
    calendarService: CurrencyPairCalendarPromiseLookupService,
    oeLogger: OELoggerService,
    public renderer2: Renderer2,
    sysConfigService: SystemConfigPromiseService,
    branchLookupService: BranchLookupService,
    configService: BfxConfigService,
    clientService: ClientLookupService,
    datasetService: OEDatasetService,
  ) {
    super(
      translate,
      settings,
      coordinator,
      traderGroupService,
      getter,
      forwardAdapter,
      utility,
      api,
      cd,
      rateService,
      rateCache,
      rateTransformer,
      rateSubscriptionService,
      currencyPairService,
      calendarService,
      oeLogger,
      renderer2,
      sysConfigService,
      branchLookupService,
      configService,
      clientService,
      datasetService,
    );

    this.oeLogger = oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initializeLabelTranslations(): void {
    super.initializeLabelTranslations();
    this.data.fixingSource.label = this.translate.instant(this.data.fixingSource.labelKey);
    this.data.fixingDate.label = this.translate.instant(this.data.fixingDate.labelKey);
    this.cd.markForCheck();
  }

  protected initializeSettings(): void {
    super.initializeSettings();
    let financialsSettings = this.settings.getSysConfigForFinancialsPanel();
    this.data.fixingDate.disabled = !financialsSettings.fixingDateModifiable;
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* RESET */

  // handleResetAllControls
  protected resetData(): void {
    super.resetData();
    this.data.fixingSource.value = null;
    this.data.fixingSource.state = null;
    this.data.fixingSource.errorMsg = null;

    this.data.fixingDate.value = null;
    this.data.fixingDate.externalState = null;
    this.data.fixingDate.errorMsg = null;

    this.data.trigger.value = null;
    this.data.trigger.externalState = null;
    this.data.trigger.state = ICNInputControlState.INACTIVE;

    // this is getting set back to Spot somewhere in the woodpile.
    this.entryType = OENEntryType.NDF;
    this.cd.markForCheck();
  }

  protected resetLocking(): void {
    this.data.fixingSource.lockState = OENLockedState.UNLOCKED;
    this.data.fixingDate.lockState = OENLockedState.UNLOCKED;
    this.data.trigger.lockState = OENLockedState.UNLOCKED;
    super.resetLocking();
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* EXTERNAL INPUT VALIDATION */

  protected handleInvalidInput(inputError: OEInputModel): void {
    super.handleInvalidInput(inputError);
    if (
      inputError != null &&
      (inputError.panel === OENPanel.PANEL_CLIENT ||
        inputError.panel === OENPanel.PANEL_FINANCIALS ||
        inputError.panel === OENPanel.PANEL_MORE)
    ) {
      switch (inputError.input) {
        case OENInputControl.FIXING_SOURCE:
          this.data.fixingSource.state = ICNInputControlState.INVALID;
          break;
        case OENInputControl.FIXING_DATE:
          this.data.fixingDate.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.TRIGGER:
          this.data.trigger.externalState = ICNInputControlState.INVALID;
          break;
      }
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.handleInvalidInput(): NULL or Invalid panel: `,
        inputError,
      );
    }
    this.cd.markForCheck();
  }
  /**********************************************************************/
  /* LOAD */

  protected loadFinancials(item: any, containsCheck: any, data: OEDLoadingDataModel): void {
    super.loadFinancials(item, containsCheck, data);
    switch (item.input) {
      case OENInputControl.FIXING_SOURCE:
        this.data.fixingSource.value = item.value;
        this.fixingSourceControl.forEach((c) => {
          c.writeValue(item.value);
        });
        this.validateFixingSource();
        break;
      case OENInputControl.FIXING_DATE:
        this.data.fixingDate.value = item.value;
        this.fixingDateControl.forEach((c) => {
          c.writeValue(item.value);
        });
        // this.validateFixingDate();
        break;
      case OENInputControl.VALUE_DATE:
        if (this.data.valueDate.isBrokenDate) {
          this.data.fixingDate.minDate = new Date();
          this.data.fixingDate.maxDate = new Date(this.data.valueDate.value.valueDate);
          this.data.fixingDate.maxDate.setDate(this.data.fixingDate.maxDate.getDate() - 1);
        }
        break;
      default:
        // no-op for debugging
        break;
    }
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* LOCKING */

  protected assignLockStateToData(lockStateItem: OEInputLockStateItemModel): void {
    let handled: boolean = false;
    switch (lockStateItem.input) {
      case OENInputControl.FIXING_SOURCE:
        this.data.fixingSource.lockState = lockStateItem.lockState;
        handled = true;
        break;
      case OENInputControl.FIXING_DATE:
        this.data.fixingDate.lockState = lockStateItem.lockState;
        handled = true;
        break;
      case OENInputControl.TRIGGER:
        this.data.trigger.lockState = lockStateItem.lockState;
        handled = true;
        break;
    }
    if (!handled) {
      super.assignLockStateToData(lockStateItem);
    }
  }

  protected updateLocking(): void {
    super.updateLocking();

    if (!this.control.lockAllFg) {
      // lockAll is INACTIVE

      if (this.data.fixingSource.lockState === OENLockedState.LOCKED) {
        this.data.fixingSource.locked = true;
        this.data.fixingDate.locked = true;
      } else if (this.data.fixingSource.lockState === OENLockedState.UNLOCKED) {
        this.data.fixingSource.locked = false;
        this.data.fixingDate.locked = false;
      }
    } else {
      // lockAll is ACTIVE
      this.data.fixingSource.locked = true;
      this.data.fixingDate.locked = true;
    }
    this.cd.markForCheck();
  }

  protected getTriggers(): Array<ICAutocompleteGenericModel> {
    let triggers: Array<ICAutocompleteGenericModel> = [];
    if (this.data.currencyPair.model) {
      if (this.data.currencyPair.model.allowNdfSpotTriggerFg) {
        triggers.push(this.data.trigger.spotTrigger);
      }
      if (this.data.currencyPair.model.allowNdfFwdTriggerFg) {
        triggers.push(this.data.trigger.outrightTrigger);
      }
    }
    return triggers;
  }

  /**********************************************************************/
  /* DATASET  */

  /**
   * Load all currency pairs from the replicate provided base lookup service
   * and fill in the appropriate currency pair values (inverted too, where applicable)
   * in the data model
   */
  protected currencyPairsGetDataset(): void {
    let dataset = this.api.getNdfCcyPairsDataset();
    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.ccyPairDatasets.sptFwdDataset = dataset.slice();
      this.data.currencyPair.dataset = dataset.slice();
    } else {
      logger.out.error("ERROR: oeSpotEntry.getNdfCcyPairsDataset(): CcyPairs dataset load failed!");
      this.data.ccyPairDatasets.sptFwdDataset = [];
      this.data.currencyPair.dataset = [];
    }
  }

  public reEvaluateFixingDate(fixingDate: any): void {
    if (fixingDate) {
      if (fixingDate instanceof Date) {
        this.data.fixingDate.minDate = new Date();
        this.data.fixingDate.maxDate = new Date(this.data.valueDate.value.valueDate);
        this.data.fixingDate.maxDate.setDate(this.data.fixingDate.maxDate.getDate() - 1);
        this.fixingDateSelected(fixingDate);
      }
    } else {
      if (this.data.valueDate.isBrokenDate) {
        this.data.fixingDate.minDate = new Date();
        this.data.fixingDate.maxDate = new Date(this.data.valueDate.value.valueDate);
        this.data.fixingDate.maxDate.setDate(this.data.fixingDate.maxDate.getDate() - 1);
        // go to server to retrieve the fixing date for the current broken date
        this.calendarService
          .getFixingDate(
            this.data.currencyPair.base,
            this.data.currencyPair.nonBase,
            this.data.valueDate.value.valueDate,
          )
          .then((value) => {
            this.fixingDateSelected(value);
          });
      } else {
        // clear the fixing date
        this.fixingDateSelected(null);
      }
    }
  }

  // @override oe-panel-spot-entry
  public reEvaluateTrigger($event: any): void {
    if (
      this.data.trigger.value == null &&
      this.data.currencyPair != null &&
      this.data.currencyPair.model instanceof CubiqTS.CurrencyPairImpl
    ) {
      const currencyPair = this.data.currencyPair.model;
      let defaultLoaded = false;

      // load defaults
      if (currencyPair.allowNdfFwdTriggerFg) {
        this.data.trigger.value = this.data.trigger.outrightTrigger;
        defaultLoaded = true;
      } else if (currencyPair.allowNdfSpotTriggerFg) {
        this.data.trigger.value = this.data.trigger.spotTrigger;
        defaultLoaded = true;
      }

      if (defaultLoaded) {
        this.cd.markForCheck();
        this.validateTrigger();
        this.onTriggerChange();
      }
    }
  }

  protected setCurrencyPair(productCode: string): boolean {
    let val = super.setCurrencyPair(productCode);
    this.validateFixingSource();
    if (productCode) this.reEvaluateTrigger(null);

    return val;
  }

  public searchFixingSources(filter: any): void {
    let fixingSources: Array<ICFixingSourceModel> = this.getFixingSources();

    let query: string = filter.query.toLowerCase();
    if (this.data.currencyPair.base && this.data.currencyPair.nonBase) {
      if (query.length > 0) {
        this.data.fixingSource.dataSet = fixingSources.filter(
          (fixingSource: any) =>
            ((fixingSource.pair == null &&
              this.settings.getSysConfigForFinancialsPanel().customFixingAllowed) ||
              fixingSource.pair == this.data.currencyPair.base + this.data.currencyPair.nonBase ||
              fixingSource.pair == this.data.currencyPair.nonBase + this.data.currencyPair.base) &&
            fixingSource.label.toLowerCase().includes(query),
        );
      } else {
        this.data.fixingSource.dataSet = fixingSources.filter((fixingSource: any) => {
          return (
            (fixingSource.pair == null &&
              this.settings.getSysConfigForFinancialsPanel().customFixingAllowed) ||
            fixingSource.pair == this.data.currencyPair.base + this.data.currencyPair.nonBase ||
            fixingSource.pair == this.data.currencyPair.nonBase + this.data.currencyPair.base
          );
        });
      }
    } else {
      this.data.fixingSource.dataSet = [];
    }
    this.cd.markForCheck();
  }

  public onClearFixingSource(): void {
    this.data.fixingSource.value = null;
    this.data.fixingSource.state = null;
    this.cd.markForCheck();
    this.onFixingSourceChange();
  }

  public onSelectFixingSource(): void {
    // this.data.traderGroup.traderGroupVisited = true;
    this.validateFixingSource();
  }

  public fixingSourceFocus(): void {
    this.data.fixingSource.state = ICNInputControlState.ACTIVE;
    this.cd.markForCheck();
  }

  public fixingSourceBlur(): void {
    this.validateFixingSource();
    this.cd.markForCheck();
  }

  public validateFixingSource(): void {
    // if(!this.control.isLoading){
    let fixingSources: Array<ICFixingSourceModel> = this.getFixingSources();

    let code: string =
      this.data.fixingSource.value instanceof ICFixingSourceModel
        ? (this.data.fixingSource.value as ICFixingSourceModel).label.toLowerCase()
        : typeof this.data.fixingSource.value === "string"
          ? this.data.fixingSource.value.toLowerCase()
          : null;
    let fixingSource: ICFixingSourceModel =
      code != null
        ? fixingSources.find((group: ICFixingSourceModel) => group.label.toLowerCase() == code)
        : null;

    if (fixingSource != null) {
      this.data.fixingSource.value = fixingSource;
    } else {
      if (!this.control.isLoading) {
        let defaultFixingSource: ICFixingSourceModel = fixingSources.find(
          (group: ICFixingSourceModel) => group.isDefault,
        );
        this.data.fixingSource.value = defaultFixingSource;
      }
    }
    if (!this.control.isLoading) {
      if (this.data.fixingSource.value == null && fixingSources.length == 1) {
        this.data.fixingSource.value = fixingSources[0];
      }
    }
    this.onFixingSourceChange();

    if (this.data.fixingSource.value == null) {
      this.data.fixingSource.state = ICNInputControlState.INACTIVE;
    } else {
      this.data.fixingSource.state = ICNInputControlState.VALID;
    }
    this.cd.markForCheck();
  }

  private getFixingSources(): Array<ICFixingSourceModel> {
    if (this.data.currencyPair && this.data.currencyPair.base && this.data.currencyPair.nonBase) {
      return this.api.loadFixingSourcesDataset().filter(
        (fixingSource: any) =>
          // reviewer note: just code formatting
          (!fixingSource.pair &&
            this.settings.getSysConfigForFinancialsPanel().customFixingAllowed) ||
          fixingSource.pair == this.data.currencyPair.base + this.data.currencyPair.nonBase ||
          fixingSource.pair == this.data.currencyPair.nonBase + this.data.currencyPair.base,
      );
    }
    return [];
  }

  public onFixingSourceChange(): void {
    if (!this.control.isLoading) {
      this.data.fixingSource.state = null;
      this.api.updateFixingSource(this.refId, this.data.fixingSource.value);
      this.cd.markForCheck();
    }
  }

  public fixingDateSelected(date): void {
    this.data.fixingDate.value = date;
    this.data.fixingDate.externalState = null;
    this.api.updateFixingDate(this.refId, this.data.fixingDate.value);
    this.cd.markForCheck();
  }

  public onCommentClientChange(): void {
    // client comment validation for NDF;
    // if fixing is "- CUSTOM -", then client comment is required
    if (this.entryType === OENEntryType.NDF) {
      if (
        this.data.fixingSource.value instanceof ICFixingSourceModel &&
        typeof this.data.fixingSource.value.label === "string" &&
        this.data.fixingSource.value.label.toUpperCase() === "- CUSTOM -" &&
        (this.data.commentClient.value == null ||
          (typeof this.data.commentClient.value === "string" &&
            this.data.commentClient.value.length === 0))
      ) {
        this.data.commentClient.externalState = 1;
      } else {
        this.data.commentClient.externalState = null;
      }
    }

    if (!this.data.commentClient.uiPrivatized) {
      this.api.updateClientComment(this.refId, this.data.commentClient.value);
    }
    this.cd.markForCheck();
  }

  public onCommentClientCheckboxChange(): void {
    this.cd.markForCheck();
    setTimeout(() => {
      if (
        typeof this.data.commentClient.checkbox.value === "boolean" &&
        this.data.commentClient.checkbox.value
      ) {
        this.data.commentClient.externalState = null;
        this.icCommentClient.nativeElement.focus();
      }
    }, 0);
  }
}
